/* ═══════════════════════════════════════════════════════════════
   SyncService — the Review feed into project copies (docs/SYNC_PLAN.md
   §2.2, §2.7, D11–D14). Thin typed wrappers over the server's bespoke sync
   actions; no maths here (the engine is shared/sync).

   §0: the server never touches a copy on its own. A Review row only OFFERS
   upstream's change; Accept lands in the copy's draft (client); Finish
   writes the copy and a History version (server, one transaction).

   Live refresh: sync rows broadcast as table `sync_feed` (never the legacy
   `spec_variant_changes` cache) — subscribe with useTableSync(['sync_feed']).
   ═══════════════════════════════════════════════════════════════ */

import type { ContentBlock, ProjectSpec } from '../../shared/spec';
import type { StandardConfig } from '../../shared/forma';
import type { Change, Decision } from '../../shared/sync';
import * as cache from './cloudCache';
import { rowToCamel } from './cloudCache';
import * as api from './apiClient';

export type SyncTargetKind = 'project_spec' | 'project_forma';
export type SyncUpstreamKind = 'master' | 'variant' | 'module';

/** One Review item as stored: the engine's Change + the remembered answer. */
export type SyncChange = Change & { decision: Decision | null };

/** One Review row (spec_variant_changes with target_kind set, camelCased). */
export interface SyncFeedRow {
  id: string;
  firmId: string;
  targetKind: SyncTargetKind;
  targetId: string;
  upstreamKind: SyncUpstreamKind;
  upstreamLabel: string | null;
  specMasterId: string | null;
  layerId: string | null;
  moduleId: string | null;
  moduleName: string | null;
  moduleFromVersion: number | null;
  moduleToVersion: number | null;
  baseVersion: number | null;
  fromVersion: number | null;
  toVersion: number | null;
  changes: SyncChange[];
  reconciledCount: number;
  status: 'pending' | 'resolved';
  /** Only when asked for (`includeContent`) — the flattened THEIRS. */
  upstreamContent?: ContentBlock[];
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A copy on a client variant whose variant has not answered upstream yet. */
export interface SyncUpstreamPending {
  targetId: string;
  layerId: string;
  label: string;
}

export interface SyncFeed {
  rows: SyncFeedRow[];
  upstreamPending: SyncUpstreamPending[];
}

const toRow = (raw: Record<string, unknown>): SyncFeedRow => {
  const row = rowToCamel({ ...raw }) as SyncFeedRow;
  row.changes = Array.isArray(row.changes) ? row.changes : [];
  row.reconciledCount = Number(row.reconciledCount) || 0;
  return row;
};

export const SyncService = {
  /** A project's Review rows (every copy in it), or one copy's. */
  feedList: async (
    scope: { projectId: string } | { targetKind: SyncTargetKind; targetId: string },
    opts: { includeResolved?: boolean; includeContent?: boolean } = {}
  ): Promise<SyncFeed> => {
    const res = await api.syncFeedList({ ...scope, ...opts });
    return {
      rows: Array.isArray(res?.rows) ? res.rows.map(toRow) : [],
      upstreamPending: Array.isArray(res?.upstreamPending) ? res.upstreamPending : [],
    };
  },

  /** Remember answers (per copy, per change id). Nothing is applied. */
  decide: async (rowId: string, decisions: Record<string, Decision | null>): Promise<SyncFeedRow> =>
    toRow(await api.syncReviewDecide({ rowId, decisions })),

  /** Finish: the copy's content (already applied into its shared draft) is
   *  written, the row resolves and a History version is kept. Idempotent.
   *  A project forma sends its whole post-Accept forma: the server keeps it
   *  as the DRAFT (never the published columns — Publish stays separate). */
  finish: async (
    rowId: string,
    decisions: Record<string, Decision | null>,
    content: ContentBlock[] | StandardConfig
  ): Promise<SyncFeedRow> =>
    toRow(
      await api.syncReviewFinish({
        rowId,
        decisions,
        content: content as unknown[] | Record<string, unknown>,
      })
    ),

  /** The project Publish gate (specs.publish). */
  publishProjectSpec: async (projectSpecId: string): Promise<ProjectSpec> => {
    const saved = rowToCamel({ ...(await api.projectSpecPublish(projectSpecId)) }) as ProjectSpec;
    // The author sees the result at once; everyone else gets the broadcast.
    cache.invalidateAndNotify('project_specs');
    return saved;
  },

  /** Publish a module's shared draft (modules.publish). */
  publishModule: async (
    moduleId: string,
    message?: string
  ): Promise<{ raised: number; synced: number }> => {
    const res = await api.modulePublish({ moduleId, message });
    return { raised: Number(res?.raised) || 0, synced: Number(res?.synced) || 0 };
  },
};
